import { useState, useEffect, useContext } from "react";
import { useLocation, Link } from "react-router-dom";
import { Modal, Form } from "react-bootstrap";
import { useAllFolders } from "../services/contexts/useFolders";
import { GroupContext } from "../services/contexts/GroupContext";
import API from "../services/api";
import { useCurrentUser } from "../services/auth";
import classes from "./TableView.module.css";
import Confirmation from "./Confirmation";
import CustomButton from "./common/CustomButton";
import ActiveInactiveWrapper from "./common/ActiveInactiveWrapper";
import LoadingSpinner from "./common/LoadingSpinner";
import { filterActiveInactiveData } from "../helpers/filterActiveInactiveData";
import { sortTableByColumn } from "../helpers/sortTableByColumn";
import MultiSelect from "./common/MultiSelect";
import UploadFile from "./UploadFile";
import { ReactComponent as NewFolderIcon } from "../svg/NewFolder.svg";
import { ReactComponent as DeleteIcon } from "../svg/Delete.svg";
import FolderItem from "./FolderItem";
import FileItem from "./FileItem";
import toast from "react-hot-toast";
import { truncateWithTooltip } from "../helpers/common";

function TableView() {
  const { data: currentUser } = useCurrentUser();

  const location = useLocation();
  const { allFolders, getAllFolders, getFilesByParent, getFoldersByParent } =
    useAllFolders();
  const { globalData } = useContext(GroupContext);

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showUploadModal, setUploadModal] = useState(false);

  const [newFolder, setNewFolder] = useState({
    folderName: "",
    selectedGroups: [],
  });

  const [itemCountLimit, setItemCountLimit] = useState(10);
  const [isActiveChecked, setIsActiveChecked] = useState(true);
  const [isDeactiveChecked, setIsDeactiveChecked] = useState(false);
  const [activeItems, setActiveItems] = useState([]);
  const [inactiveItems, setInactiveItems] = useState([]);
  const [sortOrder, setSortOrder] = useState({
    columnName: "name",
    isDescending: true,
  });

  const [itemCheck, setItemCheck] = useState([]);
  const [checkAll, setCheckAll] = useState(false);

  const { role } = currentUser;
  const { currentName, currentFolderId, parentPath } = location.state || {};

  // --- Modal Handlers ---
  const handleCreateFolderClose = () => {
    setNewFolder({ folderName: "", selectedGroups: [] });
    setShowCreateFolderModal(false);
  };
  const handleCreateFolderShow = () => setShowCreateFolderModal(true);

  const handleUploadClose = () => {
    setUploadModal(false);
    getFoldersAndFiles();
  };
  const handleUploadShow = () => setUploadModal(true);

  // --- Filter and Sort Handlers ---
  function handleFilterItems(data) {
    const [activeData, inactiveData] = filterActiveInactiveData(data);
    handleSortItems(sortOrder.columnName, activeData, inactiveData, true);
  }

  function handleSortItems(
    columnName,
    activeData,
    inactiveData,
    preservedOrder = false
  ) {
    const isSameColumn = columnName === sortOrder.columnName;

    let isDescending = isSameColumn ? !sortOrder.isDescending : true;
    if (preservedOrder) isDescending = sortOrder.isDescending;

    const [activeSorted, inactiveSorted] = sortTableByColumn(
      columnName,
      activeData,
      inactiveData,
      isDescending
    );
    if (!preservedOrder) {
      setSortOrder({ columnName, isDescending });
    }
    setActiveItems(activeSorted);
    setInactiveItems(inactiveSorted);
  }

  // --- API Call Functions ---
  const getFoldersAndFiles = async () => {
    if (!currentFolderId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const [foldersRes, filesRes] = await Promise.all([
        getFoldersByParent(currentFolderId),
        getFilesByParent(currentFolderId),
      ]);

      const foldersData = foldersRes.data.data.map((f) => ({
        ...f,
        type: "folder",
        name: f.folder_name,
      }));
      const filesData = filesRes.data.data.result.map((f) => ({
        ...f,
        type: "file",
        name: f.document_title,
      }));

      const combinedData = [...foldersData, ...filesData];
      setItems(combinedData);
      handleFilterItems(combinedData);
    } catch (e) {
      //TODO: Add additional err handling here
    } finally {
      setIsLoading(false);
    }
  };

  const createFolder = async () => {
    try {
      const response = await API.post(`/createFolder`, {
        folder_name: newFolder.folderName,
        folder_id: currentFolderId,
        path: `${parentPath}/${currentName}`,
        allowed_groups: newFolder.selectedGroups.map((group) => group.group_id),
      });
      alert(response.data.msg);
      getFoldersAndFiles();
      handleCreateFolderClose();
    } catch (e) {
      alert(e.response?.data?.msg || "Error creating folder.");
    } finally {
      getAllFolders();
    }
  };

  const deleteFolder = async (folderId, foldername) => {
    try {
      await API.delete(`/deleteFolder`, {
        data: {
          folder_id: folderId,
          folder_name: foldername,
          path: `${parentPath}/${currentName}`,
        },
      });
      getFoldersAndFiles();
    } catch (e) {
      console.error("Error deleting folder:", e);
      alert(e.response?.data?.msg || "Error deleting folder.");
    }
  };

  const deleteFile = async (fileId) => {
    try {
      const response = await API.delete(`/admin/deleteFile`, {
        data: {
          file_id: fileId,
        },
      });
      getFoldersAndFiles();
      if (response.data.message) {
        alert(response.data.message);
      }
    } catch (e) {
      console.error("Error deleting file:", e);
      alert(e.response?.data?.msg || "Error deleting file.");
    }
  };

  const handleDelete = async () => {
    for (const checkedItem of itemCheck) {
      if (checkedItem.type === "folder") {
        await deleteFolder(checkedItem.id, checkedItem.name);
      } else if (checkedItem.type === "file") {
        await deleteFile(checkedItem.id);
      }
    }
    setItemCheck([]);
    getFoldersAndFiles();
  };

  // --- useEffect for initial data load ---
  useEffect(() => {
    getFoldersAndFiles();
  }, [currentFolderId, window.location.pathname]);

  let formattedItemList = [];
  const itemCount = items.length;

  if (!isActiveChecked && isDeactiveChecked) {
    formattedItemList = inactiveItems;
  }
  if (isActiveChecked && !isDeactiveChecked) {
    formattedItemList = activeItems;
  }
  if (isActiveChecked && isDeactiveChecked) {
    formattedItemList = [...activeItems, ...inactiveItems];
  }
  formattedItemList = formattedItemList.slice(0, itemCountLimit);

  const tableContainerStyle =
    role === 1 ? classes.table_container : classes.table_container_user;
  const tableHeadStyle =
    role === 1 ? classes.table_head : classes.table_head_user;
  const tableBodyStyle =
    role === 1 ? classes.table_body : classes.table_body_user;

  const currentPath = `${parentPath}/${currentName}`
    .split("/")
    .slice(1)
    .map((el, idx) => {
      const folderState = allFolders.find((obj) => {
        return (
          obj.path ===
            `${parentPath}/${currentName}`
              .split("/")
              .slice(0, idx + 1)
              .join("/") &&
          obj.folder_name ===
            `${parentPath}/${currentName}`
              .split("/")
              .slice(0, idx + 2)
              .slice(-1)[0]
        );
      });

      return (
        <Link
          key={idx}
          className={classes.link}
          to={`${parentPath}/${currentName}`
            .split("/")
            .slice(2, idx + 2)
            .join("/")}
          state={{
            parentPath: folderState?.path,
            currentFolderId: folderState?.id,
            currentName: folderState?.folder_name,
          }}
        >
          <span>{truncateWithTooltip(el)}</span>
          <span>&gt;</span>
        </Link>
      );
    });

  return (
    <div className={classes.container}>
      <span className={classes.directory_link}> {currentPath} </span>

      {role === 1 ? (
        <div className={classes.actions_wrapper}>
          <div>
            {!isLoading &&
              `${itemCount} item${itemCount > 1 ? "s" : ""} in this directory`}
          </div>
          <div className={classes.actions_wrapper_right}>
            <ActiveInactiveWrapper
              handleOnActiveClick={() => {
                setIsActiveChecked((prev) => !prev);
              }}
              handleOnInactiveClick={() => {
                setIsDeactiveChecked((prev) => !prev);
              }}
            />
            <div className={classes.action_buttons}>
              <CustomButton
                variant="outline-primary"
                onClick={handleCreateFolderShow}
                className={classes.action_new_folder}
                style={{ width: "137px" }}
              >
                <NewFolderIcon style={{ margin: "0 3px 3px 0" }} width={25} />
                New Folder
              </CustomButton>
              <CustomButton
                variant="outline-primary"
                onClick={handleUploadShow}
              >
                New File
              </CustomButton>
              <Confirmation
                disabled={itemCheck.length <= 0}
                content={"Please confirm delete the selected items?"}
                Children={
                  <CustomButton
                    variant="outline-danger"
                    disabled={itemCheck.length <= 0}
                  >
                    <DeleteIcon
                      style={{ margin: "0 3px 3px 0" }}
                      width={26}
                      height={14}
                    />
                    Delete
                  </CustomButton>
                }
                onConfirm={handleDelete}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className={classes.item_num_wrapper}>
          {!isLoading &&
            `${itemCount} item${itemCount > 1 ? "s" : ""} in this directory`}
        </div>
      )}

      <div className={tableContainerStyle} id="custom-scroll-y">
        <table className={classes.table}>
          <thead className={tableHeadStyle}>
            <tr>
              {role === 1 && (
                <th>
                  <div className={classes.checkbox_wrapper}>
                    <input
                      type="checkbox"
                      onChange={() => {
                        setCheckAll((prev) => !prev);
                        if (!checkAll) {
                          setItemCheck(formattedItemList);
                        } else {
                          setItemCheck([]);
                        }
                      }}
                      checked={
                        checkAll &&
                        formattedItemList.length > 0 &&
                        itemCheck.length === formattedItemList.length
                      }
                    />
                  </div>
                </th>
              )}
              <th
                onClick={() =>
                  handleSortItems("name", activeItems, inactiveItems)
                }
              >
                <span>
                  Name
                  {sortOrder.columnName === "name" && (
                    <span> {sortOrder.isDescending ? " ↓" : " ↑"}</span>
                  )}
                </span>
              </th>
              <th
                onClick={() =>
                  handleSortItems("uploaded_date", activeItems, inactiveItems)
                }
              >
                <span>
                  Uploaded Date
                  {sortOrder.columnName === "uploaded_date" && (
                    <span> {sortOrder.isDescending ? " ↓" : " ↑"}</span>
                  )}
                </span>
              </th>
              <th
                onClick={() =>
                  handleSortItems("created_date", activeItems, inactiveItems)
                }
              >
                <span>
                  Created Date
                  {sortOrder.columnName === "created_date" && (
                    <span> {sortOrder.isDescending ? " ↓" : " ↑"}</span>
                  )}
                </span>
              </th>
              <th
                onClick={() =>
                  handleSortItems(
                    "last_modify_date",
                    activeItems,
                    inactiveItems
                  )
                }
              >
                <span>
                  Last Modify
                  {sortOrder.columnName === "last_modify_date" && (
                    <span> {sortOrder.isDescending ? " ↓" : " ↑"}</span>
                  )}
                </span>
              </th>
              {role === 1 && <th>Actions</th>}
            </tr>
          </thead>
          <tbody className={tableBodyStyle}>
            {isLoading ? (
              <tr>
                <td colSpan={role === 1 ? 6 : 4}>
                  <div className={classes.empty_cell}>
                    <LoadingSpinner size="sm" />
                  </div>
                </td>
              </tr>
            ) : formattedItemList.length > 0 ? (
              formattedItemList
                .filter((el) => {
                  if (isActiveChecked && isDeactiveChecked) {
                    return el;
                  } else if (!isActiveChecked && isDeactiveChecked) {
                    return el.inactive === 1;
                  } else if (isActiveChecked && !isDeactiveChecked) {
                    return el.inactive === 0;
                  }
                })
                .sort((a, b) => a.inactive - b.inactive)
                .map((el) => {
                  const isChecked = itemCheck.some(
                    (item) => item.id === el.id && item.type === el.type
                  );
                  const onCheckChange = (e) => {
                    if (e.target.checked) {
                      setItemCheck((prev) => [...prev, el]);
                    } else {
                      setItemCheck((prev) =>
                        prev.filter(
                          (item) =>
                            !(item.id === el.id && item.type === el.type)
                        )
                      );
                    }
                  };

                  return (
                    <tr key={`${el.type}-${el.id}`}>
                      {el.type === "folder" ? (
                        <FolderItem
                          folder={el}
                          role={role}
                          getFoldersByParent={getFoldersAndFiles}
                          isChecked={isChecked}
                          onCheckChange={onCheckChange}
                          deleteFolder={deleteFolder}
                        />
                      ) : (
                        <FileItem
                          file={el}
                          role={role}
                          getFiles={getFoldersAndFiles}
                          isChecked={isChecked}
                          onCheckChange={onCheckChange}
                          deleteFile={deleteFile}
                        />
                      )}
                    </tr>
                  );
                })
            ) : (
              <tr>
                <td colSpan={role === 1 ? 6 : 4}>
                  <div className={classes.empty_cell}>
                    {"No items found in this directory"}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {itemCountLimit < itemCount && (
          <div style={{ marginTop: "20px" }}>
            <CustomButton
              variant="outline-primary"
              onClick={() => setItemCountLimit((e) => e + 10)}
            >
              Load More
            </CustomButton>
          </div>
        )}
      </div>

      {/* --- Modals --- */}
      {/* Create Folder Modal */}
      <Modal
        show={showCreateFolderModal}
        onHide={handleCreateFolderClose}
        backdrop="static"
      >
        <Modal.Header className="_g-modal-header_">
          <div className="_g-close-button-anchor_">
            <div className="_g-close-button_" onClick={handleCreateFolderClose}>
              &#x2715;
            </div>
          </div>
          Create Folder
        </Modal.Header>
        <Modal.Body className="_g-modal-body_">
          <label className={classes.label}>
            Folder Name <span style={{ color: "#EF2626" }}>*</span>
          </label>
          <Form.Control
            type="text"
            autoFocus
            placeholder="New Folder Name"
            onChange={(e) =>{
              setNewFolder((prev) => ({ ...prev, folderName: e.target.value }))
              if(e.target.value.length === 55){
                toast.error("Folder name can not be longer than 55 characters");
              }
            }
            }
            maxLength={55}
            value={newFolder.folderName}
          />
          <div className={classes.group_select}>
            <MultiSelect
              label="Assign group"
              options={globalData.allGroups}
              selected={newFolder.selectedGroups}
              onChange={(groups) =>
                setNewFolder((prev) => ({ ...prev, selectedGroups: groups }))
              }
              placeholder="Please Select"
            />
          </div>
          <div className="_g-modal-button-wrapper_">
            <CustomButton
              variant="outline-secondary"
              onClick={handleCreateFolderClose}
            >
              Close
            </CustomButton>
            <CustomButton
              variant="primary"
              onClick={createFolder}
              disabled={newFolder.folderName.length === 0}
            >
              Create
            </CustomButton>
          </div>
        </Modal.Body>
      </Modal>

      {/* Upload File Modal */}
      <UploadFile
        path={`${parentPath}/${currentName}`}
        getFiles={getFoldersAndFiles}
        showUploadModal={showUploadModal}
        handleUploadClose={handleUploadClose}
        folderId={currentFolderId}
        isMeeting={false}
      />
    </div>
  );
}

export default TableView;
